import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Select, Tooltip } from 'antd'
import { get } from 'lodash'
import { segmentApi } from '@edulastic/api'
import { FlexContainer, captureSentryException } from '@edulastic/common'
import {
  IconPDLSparkle,
  IconEditMetaData,
  IconClose,
  IconCheck,
} from '@edulastic/icons'
import StandardsModal from '../../../assessment/containers/QuestionMetadata/StandardsModal'
import {
  getBulkSuggestionsSelector,
  updateBulkSuggestionAction,
  saveItemStandardAction,
  getEffectiveStandards,
} from '../ducks/bulkQuestionMetadata'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  getCurriculumsListSelector,
  standardsSelector,
} from '../../src/selectors/dictionaries'
import { getDictStandardsForCurriculumAction } from '../../src/actions/dictionaries'
import { getInterestedGradesSelector } from '../../src/selectors/user'
import { getTestEntitySelector } from '../../TestPage/ducks'
import {
  buildPdlStandardProps,
  getStandardCurriculumData,
  getStandardIdsToLog,
  getStandardsText,
  hasValidStandardSuggestion,
} from '../utils/AIQuestionAssistance'
import {
  StyledMetadataEditorRow,
  StyledMetadataEditorFields,
  StyledMetadataStandardsField,
  StyledMetadataStandardsContent,
  StyledMetadataStandardsValueRow,
  StyledStandardValueTooltipWrap,
  StyledMetadataDifficultyField,
  StyledMetadataDokField,
  StyledMetadataEditorActions,
  StyledMetadataFieldLabel,
  StyledStandardValueText,
  StyledNoStandardsText,
  StyledStandardsEditButton,
  StyledMetadataEditorSelect,
  StyledMetadataEditorSaveButton,
  StyledMetadataEditorClose,
} from './styled'

const dokOptions = DOK_OPTIONS.filter((option) => option.value !== '')
const difficultyOptions = DIFFICULTY_OPTIONS.filter(
  (option) => option.value !== ''
)

const trackMetadataSuggestionEdited = (payload) => {
  try {
    segmentApi.genericEventTrack(
      'AITestQuestionsMetadata:MetadataSuggestionEdited',
      payload,
      { attachUserData: true }
    )
  } catch (error) {
    captureSentryException(error, {
      errorMessage:
        'AI Test Questions Metadata: Error tracking MetadataSuggestionEdited event',
      sessionId: window?.sessionStorage?.kid,
    })
  }
}

const ItemBulkMetadataEditPanel = ({
  item,
  test,
  suggestions,
  updateSuggestion,
  saveItem,
  onClose,
  allCurriculums,
  interestedGrades,
  getCurriculumStandards,
  curriculumStandardsLoading,
}) => {
  const { _id: testId } = test || {}
  const itemId = item?._id
  const questions = useMemo(() => get(item, 'data.questions', []), [item])

  const editableQuestions = useMemo(
    () =>
      questions.filter((q) => hasValidStandardSuggestion(suggestions?.[q?.id])),
    [questions, suggestions]
  )

  const [
    showStandardsSelectionModal,
    setShowStandardsSelectionModal,
  ] = useState(false)
  const [standardsQuestionId, setStandardsQuestionId] = useState(null)

  const defaultGrades = interestedGrades?.length > 0 ? interestedGrades : []

  const standardsModalQuestion = useMemo(
    () =>
      editableQuestions.find(
        (question) => question.id === standardsQuestionId
      ) || null,
    [editableQuestions, standardsQuestionId]
  )

  const standardsSelectionModalProps = useMemo(() => {
    if (!standardsModalQuestion) {
      return null
    }

    const suggestion = suggestions?.[standardsModalQuestion?.id] || {}
    const selectedStandards = getEffectiveStandards(suggestion)
    const firstStandard = selectedStandards?.[0]
    const curriculum = getStandardCurriculumData(firstStandard, allCurriculums)
    const { subject, _id: curriculumId } = curriculum || {}

    return {
      selectedStandards,
      defaultStandard: {
        curriculum,
        id: curriculumId,
      },
      defaultSubject: subject,
      pdlStandardProps: buildPdlStandardProps(curriculum, defaultGrades),
    }
  }, [standardsModalQuestion, suggestions, allCurriculums, defaultGrades])

  if (!editableQuestions.length) return null

  const questionIds = editableQuestions.map((q) => q?.id).filter(Boolean)

  const handleShowStandardsSelectionModal = (value) => {
    setShowStandardsSelectionModal(value)
    if (!value) {
      setStandardsQuestionId(null)
    }
  }

  const handleOpenStandardsSelectionModal = (questionId) => {
    setStandardsQuestionId(questionId)
    setShowStandardsSelectionModal(true)
  }

  const handleStandardsApply = (data = {}) => {
    if (!standardsQuestionId) return

    const selectedStandards = data?.eloStandards || []
    handleShowStandardsSelectionModal(false)

    if (!selectedStandards.length) {
      return
    }

    const suggestion = suggestions?.[standardsQuestionId] || {}

    updateSuggestion({
      questionId: standardsQuestionId,
      updates: { standards: selectedStandards },
    })

    saveItem({ itemId, questionIds })
    onClose()

    try {
      const standardsEdited = !selectedStandards.some(
        (standard) => standard?._id === suggestion?.standard?._id
      )

      if (standardsEdited) {
        trackMetadataSuggestionEdited({
          testId,
          itemId,
          questionId: standardsQuestionId,
          standardsEdited: true,
          updatedStandardIds: getStandardIdsToLog(selectedStandards),
        })
      }
    } catch (error) {
      captureSentryException(error, {
        errorMessage:
          'AI Test Questions Metadata: Error tracking standards metadata suggestion edit',
        testId,
        itemId,
        questionId: standardsQuestionId,
        sessionId: window?.sessionStorage?.kid,
      })
    }
  }

  const handleDokChange = (questionId) => (value) => {
    const suggestedDok = suggestions?.[questionId]?.dok ?? null
    if (value === suggestedDok) return

    updateSuggestion({ questionId, updates: { dok: value } })
    trackMetadataSuggestionEdited({
      testId,
      itemId,
      questionId,
      dokEdited: true,
      updatedDok: value,
    })
  }

  const handleDifficultyChange = (questionId) => (value) => {
    const suggestedDifficulty = suggestions?.[questionId]?.difficulty ?? null
    if (value === suggestedDifficulty) return

    updateSuggestion({ questionId, updates: { difficulty: value } })
    trackMetadataSuggestionEdited({
      testId,
      itemId,
      questionId,
      difficultyEdited: true,
      updatedDifficulty: value,
    })
  }

  const handleSave = () => {
    saveItem({ itemId, questionIds })
    onClose()
  }

  return (
    <>
      {editableQuestions.map((question) => {
        const suggestion = suggestions?.[question?.id] || {}
        const selectedStandards = getEffectiveStandards(suggestion)
        const dok = suggestion.dok || undefined
        const difficulty = suggestion.difficulty || undefined
        const standardsText = getStandardsText(selectedStandards)
        const difficultyFieldId = `itemBulkMetadataEditPanelDifficulty-${question.id}`
        const dokFieldId = `itemBulkMetadataEditPanelDok-${question.id}`

        return (
          <FlexContainer
            key={question.id}
            flexDirection="column"
            width="100%"
            mt="16px"
            marginBottom="16px"
            flexProps="box-sizing: border-box; padding: 0 20px !important;"
          >
            <StyledMetadataEditorRow data-cy="itemBulkMetadataEditPanelRow">
              <StyledMetadataEditorFields>
                <StyledMetadataStandardsField>
                  <IconPDLSparkle width={24} height={24} aria-hidden="true" />
                  <StyledMetadataStandardsContent>
                    <StyledMetadataFieldLabel>
                      Standards
                    </StyledMetadataFieldLabel>
                    <StyledMetadataStandardsValueRow>
                      {selectedStandards.length ? (
                        <StyledStandardValueTooltipWrap>
                          <Tooltip title={standardsText}>
                            <StyledStandardValueText data-cy="itemBulkMetadataEditPanelStandardValue">
                              {standardsText}
                            </StyledStandardValueText>
                          </Tooltip>
                        </StyledStandardValueTooltipWrap>
                      ) : (
                        <StyledNoStandardsText data-cy="itemBulkMetadataEditPanelNoStandards">
                          No standards selected
                        </StyledNoStandardsText>
                      )}
                      <StyledStandardsEditButton
                        type="button"
                        data-cy="itemBulkMetadataEditPanelEditStandards"
                        aria-label="Edit standards"
                        onClick={() =>
                          handleOpenStandardsSelectionModal(question.id)
                        }
                      >
                        <IconEditMetaData
                          width={16}
                          height={16}
                          fill="#505e6f"
                          aria-hidden
                        />
                      </StyledStandardsEditButton>
                    </StyledMetadataStandardsValueRow>
                  </StyledMetadataStandardsContent>
                </StyledMetadataStandardsField>

                <StyledMetadataDifficultyField>
                  <StyledMetadataFieldLabel htmlFor={difficultyFieldId}>
                    Difficulty
                  </StyledMetadataFieldLabel>
                  <StyledMetadataEditorSelect
                    id={difficultyFieldId}
                    data-cy="itemBulkMetadataEditPanelDifficulty"
                    value={difficulty}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 2000 }}
                    onChange={handleDifficultyChange(question.id)}
                  >
                    {difficultyOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </StyledMetadataEditorSelect>
                </StyledMetadataDifficultyField>

                <StyledMetadataDokField>
                  <StyledMetadataFieldLabel htmlFor={dokFieldId}>
                    DoK
                  </StyledMetadataFieldLabel>
                  <StyledMetadataEditorSelect
                    id={dokFieldId}
                    data-cy="itemBulkMetadataEditPanelDok"
                    value={dok}
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 2000 }}
                    onChange={handleDokChange(question.id)}
                  >
                    {dokOptions.map((option) => (
                      <Select.Option key={option.label} value={option.label}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </StyledMetadataEditorSelect>
                </StyledMetadataDokField>
              </StyledMetadataEditorFields>

              <StyledMetadataEditorActions>
                <StyledMetadataEditorSaveButton
                  type="button"
                  data-cy="itemBulkMetadataEditPanelSave"
                  aria-label="Save metadata"
                  onClick={handleSave}
                >
                  <IconCheck
                    width={16}
                    height={16}
                    color="#158463"
                    aria-hidden
                  />
                  Save
                </StyledMetadataEditorSaveButton>
                <StyledMetadataEditorClose
                  type="button"
                  data-cy="itemBulkMetadataEditPanelClose"
                  aria-label="Close without saving"
                  onClick={onClose}
                >
                  <IconClose
                    width={12}
                    height={12}
                    color="#151d34"
                    aria-hidden
                  />
                </StyledMetadataEditorClose>
              </StyledMetadataEditorActions>
            </StyledMetadataEditorRow>
          </FlexContainer>
        )
      })}

      {showStandardsSelectionModal && standardsSelectionModalProps && (
        <StandardsModal
          defaultSubject={standardsSelectionModalProps.defaultSubject}
          defaultGrades={defaultGrades}
          defaultStandards={standardsSelectionModalProps.selectedStandards}
          defaultStandard={standardsSelectionModalProps.defaultStandard}
          pdlStandardProps={standardsSelectionModalProps.pdlStandardProps}
          visible={showStandardsSelectionModal}
          curriculums={allCurriculums}
          onApply={handleStandardsApply}
          setSubject={() => {}}
          onCancel={() => handleShowStandardsSelectionModal(false)}
          getCurriculumStandards={getCurriculumStandards}
          curriculumStandardsLoading={curriculumStandardsLoading}
          showAllInterestedCurriculums
          zIndex={2000}
        />
      )}
    </>
  )
}

ItemBulkMetadataEditPanel.propTypes = {
  item: PropTypes.object.isRequired,
  test: PropTypes.object,
  suggestions: PropTypes.object,
  updateSuggestion: PropTypes.func.isRequired,
  saveItem: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  allCurriculums: PropTypes.array,
  interestedGrades: PropTypes.array,
  getCurriculumStandards: PropTypes.func.isRequired,
  curriculumStandardsLoading: PropTypes.bool,
}

ItemBulkMetadataEditPanel.defaultProps = {
  test: null,
  suggestions: {},
  allCurriculums: [],
  interestedGrades: [],
  curriculumStandardsLoading: false,
}

export default connect(
  (state) => ({
    test: getTestEntitySelector(state),
    suggestions: getBulkSuggestionsSelector(state),
    allCurriculums: getCurriculumsListSelector(state),
    interestedGrades: getInterestedGradesSelector(state),
    curriculumStandardsLoading: standardsSelector(state)?.loading,
  }),
  {
    updateSuggestion: updateBulkSuggestionAction,
    saveItem: saveItemStandardAction,
    getCurriculumStandards: getDictStandardsForCurriculumAction,
  }
)(ItemBulkMetadataEditPanel)
